<template>
  <label
    ref="label"
    :data-id="randomId"
    :data-type="type"
    :class="classObject">

    <!-- Custom icon first -->
    <svg v-if="icon" class="field-icon" :class="{'icon-right': iconRight}"><use :xlink:href="'#' + icon"></use></svg>

    <input
      v-if="isRegularInput"
      ref="mainInput"
      :id="id"
      :type="inputType"
      :value="visibleValue"
      :class="{centeredText: centeredText}"
      @input="setNewValue"
      @keyup="toggleAccesibilityDropdown"
      @keyup.enter="$emit('enter')"
      @blur="$emit('blur')"
      :readonly="isReadOnly"
      :disabled="disabled">

    <input
      v-if="inputType === 'number'"
      ref="numInput"
      :id="id"
      type="number"
      :min="min"
      :max="max"
      :value="visibleValue"
      @input="setNewValue"
      @keyup="toggleAccesibilityDropdown"
      @focus="receivedFocus(...arguments, true)"
      @blur="receivedFocus(...arguments, false)"
      :class="{centeredText: centeredText, isFocused}"
      :readonly="isReadOnly"
      title=""
      :disabled="disabled">

    <input
      v-if="inputType === 'checkbox'"
      :id="id"
      type="checkbox"
      :value="value"
      :checked="value"
      @click="setNewValue"
      :readonly="isReadOnly"
      :disabled="disabled">

    <div
      v-if="pmValue && !disabled"
      class="plus-minus">
      <btn icon="minus" small invert @click.native="doPM(-1)"/>
      <btn icon="plus" small invert @click.native="doPM(1)"/>
    </div>

    <!-- Dropdown zone-->
    <input
      v-if="isDropDown"
      type="checkbox"
      class="close-by-overlay"
      ref="checkbox"
      :id="id"
      :disabled="disabled"
      @change="dropEvent">
    <div
      v-if="isDropDown"
      class="revealed-dropdown"
      :class="{above: opensAbove, search: hasSearch}"
      @click="dropClick()">

      <input
        v-if="isDropDown && hasSearch"
        v-model="searchText"
        ref="search"
        type="text"
        class="search-box gray thin"
        @keyup="trySelectFromSearch">

      <div
        :style="styleDropdown"
        class="options-scroll">
        <span
          v-for="(item, index) in optionsPossiblyFiltered"
          :key="index"
          :data-value="item.value || item">
          <a
            :class="{'selected': (( value ? value.value || value : value ) === (item.value || item)) || ((item.value || item) === (selectedFromDropdown.value || selectedFromDropdown))}"
            @click="setResultOption(item)">{{ item.label || item }}</a>
        </span>

<!--        Empty list -->
        <span
          v-if="!optionsPossiblyFiltered.length"
          class="empty-list">
          <svg
            class="w-4 h-4">
              <use :xlink:href="'#cancel'"></use>
            </svg>
        </span>
      </div>

      <!-- end of dropdown element -->
    </div>

    <!-- Path browse button -->
    <btn
      v-if="type === 'path'"
      :disabled="disabled"
      icon="open" small @click.native="openBrowseDialog()"/>
    <!-- Delete button for path -->
    <btn
      v-if="type === 'path'"
      :disabled="disabled || !value"
      icon="cancel" small @click.native="clearCurrentPath()"/>

    <!-- Spinner -->
    <div
      v-if="hasSpinner"
      class="spinner"></div>

    <!-- Simple numeric keyboard (if enabled) -->
    <!-- will eventually include numeric and standard mode -->
    <NumericKeyboard
      v-if="keyboard"
      class="osk"
      @digit="receiveKeyboardInput"/>

    <!-- Has to be last for CSS ~ selection to work -->
    <i
      v-if="!noLabel"
      :class="{'error': error, 'auto-label': autoLabel,'label-3': label3,'label-10': label10, 'label-20': label20, 'label-15': label15}">{{label}}</i>
  </label>
</template>

<script>
  import server from '@/delphi-server'
  import { EventBus } from '@/event-bus.js'
  import Btn from '@/components/basic/Btn'
  import KeyboardNumeric from '@/components/basic/KeyboardNumeric'
  import NumericKeyboard from './KeyboardNumeric'

  export default {
    components: {
      NumericKeyboard,
      Btn,
      KeyboardNumeric
    },
    props: {
      id: String,
      label: [String, Number],
      type: {
        type: String,
        default: 'text',
        validator (value) {
          if (!value) { return true }
          // checkbox comes in as input-type
          return (['', 'text', 'number', 'dropdown', 'path'].indexOf(value) !== -1)
        }
      },
      triggersConfigSave: Boolean,
      options: Array,
      hasSearch: Boolean,
      error: Boolean,
      value: [String, Number, Object, Boolean],
      maxItems: [String, Number],
      readonly: Boolean,
      alignright: Boolean,
      disabled: Boolean,
      autoLabel: Boolean, // make the label width automatic
      label3: Boolean,
      label10: Boolean,
      label15: Boolean,
      label20: Boolean,
      opensAbove: {
        type: Boolean,
        default: false
      },
      noLabel: {
        type: Boolean,
        default: false
      },
      translatedLabel: Boolean,
      /* if set, it will show plus/minus buttons */
      pmValue: {
        type: Number,
        default: 0
      },
      /* turn on rounding for +- buttons for integers */
      pmRound: {
        type: Boolean,
        default: false
      },
      inputType: {
        type: String,
        default: 'text'
      },
      /* type = number fields */
      min: Number,
      max: Number,
      webUnitsNumber: {
        /* special case for showing web units, units / 2 */
        type: Boolean,
        default: false
      },
      decimals: {
        type: Number,
        default: 1
      },
      // mask for path-type fields
      // transforms the dialog from folder to file select
      fileMask: String,
      // for dropdowns where selection is not fixed
      forceAllowText: Boolean,
      icon: String,
      iconRight: Boolean,
      centeredText: Boolean,
      singleCheckbox: Boolean,
      hasSpinner: Boolean,
      keyboard: Boolean,
      selectAllOnEnter: Boolean
    },
    data () {
      return {
        randomId: 'field-' + this._uid,
        searchText: '',
        isOpen: false,
        selectedFromDropdown: '',
        selectedIndex: null,
        saveTimer: null,
        saveTimeout: 1500,
        isFocused: false,
        justClickedButton: false,
        firstKeyboardClick: false
      }
    },
    mounted () {
      /* this should be refactored more nicely */
      if (this.isDropDown && !this.disabled) {
        const activeItem = this.$el.querySelector('span[data-value="' + this.value + '"]')
        activeItem && activeItem.scrollIntoView()
      }

      /* if field is type path, listed for updates from host */
      if (this.type === 'path') {
        this.listenForInputChanges()
      }

      EventBus.$on('needsfocus-' + this.randomId, this.fieldNeedsFocus)
    },
    destroyed () {
      if (this.saveTimer) {
        clearTimeout(this.saveTimer)
      }
      /* not necessarily on, but seems to ignore that it's not set */
      EventBus.$off('pathchanged-' + this.randomId, this.pathValueUpdated)
      EventBus.$off('needsfocus-' + this.randomId, this.fieldNeedsFocus)
    },
    methods: {
      clearCurrentPath () {
        this.pathValueUpdated('')
        this.$emit('clear')
      },
      checkIfConfigNeedsSaving () {
        // console.log('triggering save')
        if (!this.triggersConfigSave) {
          return false
        }
        // console.log('Clearing timer', this.saveTimer)
        if (this.saveTimer) {
          clearTimeout(this.saveTimer)
        }
        this.saveTimer = setTimeout(this.triggerConfigSave, this.saveTimeout)
      },
      triggerConfigSave () {
        if (this.saveTimer) {
          clearTimeout(this.saveTimer)
        }
        EventBus.$emit('save-config-from-field', {})
      },
      trySelectFromSearch (ev) {
        if (ev.keyCode === 38) {
          // up
          if (this.selectedIndex === null) {
            this.selectedIndex = 0
          } else {
            this.selectedIndex--
          }
          if (this.selectedIndex < 0) {
            this.selectedIndex = this.optionsPossiblyFiltered.length - 1
          }
          this.selectedFromDropdown = this.optionsPossiblyFiltered[this.selectedIndex]
          this.bringInViewport(this.selectedFromDropdown)
        }
        if (ev.keyCode === 40) {
          // down
          if (this.selectedIndex === null) {
            this.selectedIndex = 0
          } else {
            this.selectedIndex++
          }
          if (this.selectedIndex >= this.optionsPossiblyFiltered.length) {
            this.selectedIndex = 0
          }
          this.selectedFromDropdown = this.optionsPossiblyFiltered[this.selectedIndex]
          this.bringInViewport(this.selectedFromDropdown)
        }
        if (ev.keyCode === 13) {
          // enter
          if (this.optionsPossiblyFiltered.length === 1) {
            this.setResultOption(this.optionsPossiblyFiltered[0])
          } else {
            // if something is selected with the keyboard, use tht
            if (this.selectedFromDropdown) {
              this.setResultOption(this.selectedFromDropdown)
            }
          }
        }
        if (ev.code === 'Escape') {
          EventBus.$emit('overlayToggle', false)
          this.$refs.checkbox.checked = false
        }
      },
      listenForInputChanges () {
        EventBus.$on('pathchanged-' + this.randomId, this.pathValueUpdated)
      },
      fieldNeedsFocus () {
        if (this.$refs.checkbox) {
          this.$refs.checkbox.checked = true
        }
        this.$refs.label.focus()
      },
      pathValueUpdated (data) {
        this.$emit('input', data)
        this.checkIfConfigNeedsSaving()
      },
      openBrowseDialog () {
        if (this.fileMask) {
          server.callback('FileDialogDI', JSON.stringify({id: this.randomId, mask: this.fileMask}))
        } else {
          server.callback('FolderBrowserDialogDI', this.randomId)
        }
      },
      dropClick () {},
      setNewValue (event) {
        // this would be THE place to do numeric validation
        // data tata, IN SFARSIT
        // console.log('New value', event.srcElement.value, 'old', this.value)
        // event.srcElement.value = this.value
        // return false
        // console.log('Changing value', this.triggersConfigSave)
        if (this.enforceNumericRules(event.srcElement.value)) { return false }
        if (this.isCheckbox) {
          let isChecked = this.value
          // first time it comes as string not as boolean
          if (isChecked === 'false') { isChecked = false }
          if (isChecked === 'true') { isChecked = true }
          this.$emit('input', !isChecked)
          this.checkIfConfigNeedsSaving()
          return true
        }
        this.$emit('input', event.srcElement.value)
        this.checkIfConfigNeedsSaving()
      },
      dropEvent (event) {
        // make this first so list is rendered properly
        this.searchText = ''
        this.selectedIndex = null
        this.selectedFromDropdown = ''
        if (this.hasSearch) {
          this.$refs.search.focus()
        }
        // scroll element into view
        // make it on next tick so the search text has time to clear
        // and the proper item list rendered
        this.$nextTick(() => {
          this.bringInViewport(this.value)
          // const activeItem = this.$el.querySelector('span[data-value="' + (this.value.value || this.value) + '"]')
          // console.log('Scrolling into view', activeItem, this.value.value || this.value)
          // activeItem && activeItem.scrollIntoView()
        })
        EventBus.$emit('overlayToggle', event.srcElement.checked)
      },
      bringInViewport (value) {
        const activeItem = this.$el.querySelector('span[data-value="' + (value.value || value) + '"]')
        // console.log('Scrolling into view', activeItem, this.value.value || this.value)
        activeItem && activeItem.scrollIntoView()
      },
      setResultOption (value) {
        EventBus.$emit('overlayCloseForPopup')
        /* assign either label or value directly */
        if (this.translatedLabel) {
          this.$emit('input', value.value)
        } else {
          this.$emit('input', value)
        }
        this.checkIfConfigNeedsSaving()
        this.$refs.checkbox.checked = false
      },
      enforceNumericRules (value) {
        if (this.isInteger) {
          // console.log('Enforcing numeric rules if neces', value)
          this.$emit('input', parseFloat(value))
          return true
          // this.value = parseFloat(this.value)
        }
      },
      toggleAccesibilityDropdown (key) {
        if (!this.isDropDown) { return true }
        // console.log('Key', key.code)
        if (key.code === 'Tab') {
          this.$refs.checkbox.checked = !this.$refs.checkbox.checked
        }
        if (key.code === 'Escape') {
          EventBus.$emit('overlayToggle', false)
          this.$refs.checkbox.checked = false
        }
      },
      doPM (modifier) {
        let newValue = (Number.isInteger(this.pmValue)) ? (parseInt(this.value) + (modifier * this.pmValue)) : (parseFloat(this.value) + (modifier * this.pmValue)).toFixed(this.decimals)
        if (this.pmRound) {
          newValue = Math.round(newValue)
        }
        let cancelNewValue = false
        if ((typeof this.min !== 'undefined') && (newValue < this.min)) {
          cancelNewValue = true
        }
        if ((typeof this.max !== 'undefined') && (newValue > this.max)) {
          cancelNewValue = true
        }
        if (!cancelNewValue) {
          // transform back to numeric if needed
          if (this.inputType === 'number') {
            newValue = parseFloat(newValue)
          }
          this.$emit('input', newValue)
          this.checkIfConfigNeedsSaving()
        }
      },
      receivedFocus (el, value) {
        if (value && this.selectAllOnEnter && (!this.isFocused)) {
          el.target.select()
        }

        if (this.keyboard) {
          if (value) {
            this.isFocused = true
          } else {
            // when turning off, give time for the osk button click event to happen
            // console.log('Trying to turn off')
            setTimeout(() => {
              if (this.justClickedButton === false) {
                this.isFocused = false
                this.firstKeyboardClick = false
              } else {
                el.target.focus()
              }
              console.log('Setting firstKeyboardClick to false')
              this.justClickedButton = false
            }, 150)
          }
        }
      },
      receiveKeyboardInput (value) {
        console.log('Got keyboard input', this.value, value, this.firstKeyboardClick)
        if (value === -1) {
          // return true
          const existingValue = '' + this.value
          this.$emit('input', existingValue.slice(0, -1))
          this.justClickedButton = true
          return true
        } else if (value === -2) {
          // clicked ok, close
        } else {
          if (this.firstKeyboardClick === false) {
            this.$emit('input', value)
          } else {
            let newValue = parseInt('' + this.value + value)
            if (isNaN(newValue)) {
              newValue = value
            }
            console.log('Append new value')
            this.$emit('input', newValue)
          }
          this.justClickedButton = true
          this.firstKeyboardClick = true
        }
      }
    },
    computed: {
      classObject () {
        return {
          singleCheckbox: this.singleCheckbox,
          dropdown: this.isDropDown,
          alignright: this.alignright,
          checkbox: this.inputType === 'checkbox',
          readonly: this.readonly
        }
      },
      isRegularInput () {
        return (['text', 'password'].indexOf(this.inputType) > -1)
      },
      optionsPossiblyFiltered () {
        if (!this.hasSearch) { return this.options }
        return this.options.filter(item => {
          const mainText = item.label || item.value || item
          return mainText.toLowerCase().indexOf(this.searchText.toLowerCase()) > -1
        })
      },
      visibleValue () {
        if (this.webUnitsNumber) { return Math.round(this.value / 2) }
        if ((this.value) && (this.value.label) && (typeof this.value.label === 'string')) { return this.value.label }
        // handle case when translation is needed
        if (this.translatedLabel) {
          let snipText = this.value
          this.options.forEach(item => {
            if (item.value === this.value) { snipText = item.label }
          })
          return snipText
        }

        return this.value
      },
      isDropDown () {
        return this.type === 'dropdown'
      },
      isInteger () {
        return this.type === 'number'
      },
      isCheckbox () {
        return this.inputType === 'checkbox'
      },
      isReadOnly () {
        return (this.isDropDown || this.readonly) && (!this.forceAllowText)
      },
      hasOverflow () {
        if (!this.options || !this.maxItems) { return false }
        return (this.options.length > this.maxItems)
      },
      styleDropdown () {
        return {
          height: (3.23 * this.maxItems) + 'rem',
          maxHeight: (3.23 * this.maxItems) + 'rem',
          overflowY: (this.hasOverflow ? 'scroll' : 'visible')
        }
      }
    }
  }
</script>

<style lang="scss" scoped>
  $paddingText: 0.5rem;

  label {
    display: flex;
    height: $size-grid / 2;
    position: relative;
    cursor: text;
    background: $gray-very-light;

    &.dropdown:after {
      top: calc(50% - 4px);
      content: "";
      width: 0;
      height: 0;
      border-left: 10px solid transparent;
      border-right: 10px solid transparent;
      border-top: 10px solid $gray-medium;
      position: absolute;
      cursor: pointer;
      right: 15px;
      pointer-events: none;
    }

    &.alignright {
      input[type="text"],
      input[type="password"],
      input[type="number"] {
        text-align: right;

      }

      &.dropdown {
        input[type="text"] {
          padding-right: 3rem;
        }
      }
    }

    &.singleCheckbox {
      width: 4rem !important;
      background: transparent;
    }

    &.checkbox {
      cursor: pointer;

      input[type=checkbox] {
        cursor: pointer;
        position: absolute;
        top: 0;
        left: 0;
        bottom: 0;
        margin: 0;
        width: 100%;
        height: 100%;
        -webkit-appearance: none;

        &:focus {
          outline: none;
        }

        & + i {
          width: 100%;
        }

        & + i:before {
          position: absolute;
          right: 0;
          content: "";
          width: 65px;
          top: -.7rem;
          height: 65px;
          background-size: cover;
          transform: rotate(180deg);
          background-image: url(data:image/svg+xml;base64,PHN2ZyB2ZXJzaW9uPSIxLjEiIGlkPSJMYXllcl8xIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHhtbG5zOnhsaW5rPSJodHRwOi8vd3d3LnczLm9yZy8xOTk5L3hsaW5rIiB4PSIwcHgiIHk9IjBweCIgd2lkdGg9IjI1MHB4IiBoZWlnaHQ9IjI1MHB4IiB2aWV3Qm94PSIwIDAgMjUwIDI1MCIgZW5hYmxlLWJhY2tncm91bmQ9Im5ldyAwIDAgMjUwIDI1MCIgeG1sOnNwYWNlPSJwcmVzZXJ2ZSI+PGc+PGc+PHBhdGggZmlsbD0iI0ZGRkZGRiIgc3Ryb2tlPSIjRUEwMDAwIiBzdHJva2Utd2lkdGg9IjciIHN0cm9rZS1taXRlcmxpbWl0PSIxMCIgZD0iTTIxNS40MTcsMTI1Ljc1YzAsMjUuODUtMjEuMTUsNDctNDcsNDdIODEuNTgzYy0yNS44NSwwLTQ3LTIxLjE1LTQ3LTQ3di0xLjVjMC0yNS44NSwyMS4xNS00Nyw0Ny00N2g4Ni44MzRjMjUuODUsMCw0NywyMS4xNSw0Nyw0N1YxMjUuNzV6Ii8+PC9nPjxjaXJjbGUgZmlsbD0iI0VBMDAwMCIgY3g9IjE2Ni44MiIgY3k9IjEyNSIgcj0iMzgiLz48L2c+PC9zdmc+);
        }

        &:checked {
          & + i:before {
            background-image: url(data:image/svg+xml;base64,PHN2ZyB2ZXJzaW9uPSIxLjEiIGlkPSJMYXllcl8xIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHhtbG5zOnhsaW5rPSJodHRwOi8vd3d3LnczLm9yZy8xOTk5L3hsaW5rIiB4PSIwcHgiIHk9IjBweCIgd2lkdGg9IjI1MHB4IiBoZWlnaHQ9IjI1MHB4IiB2aWV3Qm94PSIwIDAgMjUwIDI1MCIgZW5hYmxlLWJhY2tncm91bmQ9Im5ldyAwIDAgMjUwIDI1MCIgeG1sOnNwYWNlPSJwcmVzZXJ2ZSI+PGc+PHBhdGggZmlsbD0iI0ZGRkZGRiIgc3Ryb2tlPSIjMDBDRjAwIiBzdHJva2Utd2lkdGg9IjciIHN0cm9rZS1taXRlcmxpbWl0PSIxMCIgZD0iTTIxNS40MTcsMTI1Ljc1YzAsMjUuODUtMjEuMTUsNDctNDcsNDdIODEuNTgzYy0yNS44NSwwLTQ3LTIxLjE1LTQ3LTQ3di0xLjVjMC0yNS44NSwyMS4xNS00Nyw0Ny00N2g4Ni44MzRjMjUuODUsMCw0NywyMS4xNSw0Nyw0N1YxMjUuNzV6Ii8+PC9nPjxjaXJjbGUgZmlsbD0iIzAwQ0YwMCIgY3g9IjgyLjk0NiIgY3k9IjEyNSIgcj0iMzgiLz48L3N2Zz4=);
          }
        }

        &[disabled] {
          & + i:before {
            filter: grayscale(1);
            opacity: 0.5;
          }
        }
      }

      &.readonly {
        pointer-events: none;
      }
    }
  }

  i {
    font-size: 1.3rem;
    line-height: 2.5rem;
    padding: 0 0 0 $paddingText;
    flex: none;
    order: -1;
    width: auto;
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
    font-style: normal;
    pointer-events: none;

    &:after {
      transition: box-shadow 0.2s ease-in-out;
      content: "";
      position: absolute;
      left: 0;
      top: 0;
      width: 100%;
      bottom: 0;
      box-shadow: none;
      pointer-events: none;
    }

    &.auto-label {
      // not used anymore
      width: auto;
    }

    &.error {
      color: $color-error;
    }

    &.label-3 {
      width: 3rem;
    }

    &.label-10 {
      width: 10rem;
    }

    &.label-15 {
      width: 15rem;
    }

    &.label-20 {
      width: 20rem;
    }
  }

  .revealed-dropdown {
    visibility: hidden;
    opacity: 0;
    transition: opacity 0.2s linear;
    background: $cyan;
    position: absolute;
    width: 100%;
    left: 0;
    height: auto;
    top: $size-grid / 2;
    z-index: 101;
    cursor: default;
    display: flex;
    flex-direction: column;
    padding: $size-grid / 10 $paddingText $paddingText;
    border-radius: 12px;
    /*overflow-y: scroll;*/

    &.above {
      top: auto;
      bottom: $size-grid / 2;
    }

    &::-webkit-scrollbar:disabled {
      display: none;
    }

    span {
      padding-top: $paddingText;
      display: block;

      &:first-child {
        /*<!--padding-top: $paddingText;-->*/
      }
    }

    a {
      display: block;
      background: #0071bc;
      font-family: "SourceSansSemiBold";
      color: #fff;
      text-align: center;
      font-size: 1.3rem;
      line-height: 1.6rem;
      padding: $size-grid / 10;
      border-radius: 10.625px;
      cursor: pointer;
      position: relative;


      & + a {
        margin-top: $paddingText;
      }

      &.selected,
      &:hover {
        background-color: $magenta;
      }

    }

    /* only inside dom when active */
    .search-box {
      background-color: $white;
      margin: 0 0 0.5rem;
      padding: 0.3rem 0 0.3rem 3rem;
    }

    &.search {
      padding-top: $size-grid / 8;
    }
  }

  input[type="number"]::-webkit-outer-spin-button,
  input[type="number"]::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  input[type=text],
  input[type=password],
  input[type=number] {
    flex: 1;
    background-color: inherit;
    border: none;
    outline: none;
    height: 42.5px;
    width: 100%;
    font-family: "SourceSansSemiBold";
    font-size: 1.3rem;
    color: #555;
    padding: 0 $paddingText;

    &:focus {

      ~ i {
        color: $cyan;
      }

      + i:after {
        box-shadow: -1px 1px 1px 0 #c9cacb inset, 0 -1px 1px 0 #c9cacb inset;
      }
    }

    &.isFocused {
      ~ .osk {
        display: flex !important;
      }
    }

    &[disabled] {
      color: $gray-medium;
    }

    &.centeredText {
      text-align: center;
    }

  }

  label.dropdown {
    input[type=checkbox] {
      position: absolute;
      top: 0;
      left: 0;
      bottom: 0;
      margin: 0;
      width: 100%;
      height: 100%;
      -webkit-appearance: none;

      &:focus {
        outline: none;
      }

      &:checked {
        ~ i {
          color: $cyan;
        }

        & ~ i:after {
          box-shadow: -1px 1px 1px 0 #c9cacb inset, 0 -1px 1px 0 #c9cacb inset;
        }
      }

      &:checked ~ .revealed-dropdown {
        visibility: visible;
        opacity: 1;
      }
    }
  }

  .plus-minus {
    .button:first-child {
      margin-right: -2px;
    }

    .button {
      cursor: pointer;
    }
  }

  .empty-list {
    svg {
      fill: $gray-light;
      position: absolute;
      left: calc(50% - 6rem);
    }
  }

  .field-icon {
    width: 45px;
    height: 45px;

    &.icon-right {
      position: absolute;
      right: 0;
      top: 0;

      &+input {
        margin-right: 42px;
      }
    }
  }

  .osk {
    display: none !important;
  }
</style>



// WEBPACK FOOTER //
// src/components/basic/Field.vue